import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  useGetProviderApiCredentialsQuery,
  useCreateApiCredentialMutation,
  useDeactivateApiCredentialMutation,
  ProviderApiCredentialDataFragment,
  ProvidersApiCredentialStatus,
} from '../../../generated/graphql';
import { Transition } from '@headlessui/react';
import { defaultTransitionProps } from '../../lib/animation';
import {
  PlusSmIcon,
  TrashIcon,
  ClipboardIcon,
  CheckIcon,
} from '@heroicons/react/outline';
import classNames from 'classnames';
import Button from '../../components/Button';
import toast from 'react-hot-toast';
import ToastAlert from '../../components/ToastAlert';
import Loading from '../Loading';
import Modal from '../../components/Modal';
import IconButton from '../../components/IconButton';
import { getShortDate, getTimeStamp } from '../../lib/time';
import InputGroup from '../../components/InputGroup';
import Spinner from '../../svgs/Spinner';
import { copyToClipboard } from '../../../v2/lib/clipboard';

interface NewCredential {
  apiClientId: string;
  secret: string;
  name: string;
}

interface CredentialFormData {
  name: string;
  description?: string;
}

const DevelopersSection: FC = () => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [copiedField, setCopiedField] = useState<'clientId' | 'secret' | null>(
    null,
  );
  const [newCredential, setNewCredential] = useState<NewCredential | null>(
    null,
  );
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isConfirmDeactivateModalOpen, setIsConfirmDeactivateModalOpen] =
    useState(false);
  const [credentialToDeactivate, setCredentialToDeactivate] =
    useState<ProviderApiCredentialDataFragment | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CredentialFormData>({
    defaultValues: {
      name: '',
      description: '',
    },
  });

  const { data, loading, error, refetch } = useGetProviderApiCredentialsQuery({
    onError(error) {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="Something went wrong loading API credentials. Please try refreshing."
          level="error"
        />
      ));
    },
  });

  const [createApiCredentialMutation, { loading: isCreating }] =
    useCreateApiCredentialMutation({
      onCompleted: (data) => {
        const response = data.providersCreateApiCredential;

        // Check for error in response
        if (response?.error) {
          toast.custom(({ visible }) => (
            <ToastAlert
              isVisible={visible}
              message={response.error || 'Failed to create API credential'}
              level="error"
            />
          ));
          return;
        }

        // Check for success with data
        if (response?.success && response?.apiCredential) {
          const apiCredential = response.apiCredential;

          // Check if secret is available (only on creation)
          if (apiCredential.secret) {
            // Set the new credential which will change the modal content
            setNewCredential({
              apiClientId: apiCredential.apiClientId,
              secret: apiCredential.secret,
              name: apiCredential.name,
            });
          }
        }

        refetch();
      },
      onError: (error) => {
        toast.custom(({ visible }) => (
          <ToastAlert
            isVisible={visible}
            message={error.message || 'Failed to create API credential'}
            level="error"
          />
        ));
      },
    });

  const [deactivateApiCredentialMutation] = useDeactivateApiCredentialMutation({
    onCompleted: () => {
      refetch();
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="API credential deactivated successfully"
          level="success"
        />
      ));
    },
    onError: (error) => {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message={error.message || 'Failed to deactivate API credential'}
          level="error"
        />
      ));
    },
  });

  const apiCredentials = data?.providersApiCredentials ?? [];

  const handleCreateApiCredential = () => {
    setNewCredential(null);
    reset({ name: '', description: '' });
    setIsCreateModalOpen(true);
  };

  const handleCreateApiCredentialSubmit = handleSubmit((data) => {
    // Call the mutation - the onCompleted callback will update the modal content
    createApiCredentialMutation({
      variables: {
        input: {
          name: data.name.trim(),
          description: data.description?.trim() || undefined,
        },
      },
    });
  });

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    reset({ name: '', description: '' });
    setNewCredential(null);
  };

  const handleDeactivateClick = (
    apiCredential: ProviderApiCredentialDataFragment,
  ) => {
    setCredentialToDeactivate(apiCredential);
    setIsConfirmDeactivateModalOpen(true);
  };

  const handleConfirmDeactivate = () => {
    if (!credentialToDeactivate) return;

    deactivateApiCredentialMutation({
      variables: {
        apiClientId: credentialToDeactivate.apiClientId,
      },
    });

    setIsConfirmDeactivateModalOpen(false);
    setCredentialToDeactivate(null);
  };

  const formatDate = (date: string | null | undefined) => {
    if (!date) return 'Never';
    return getShortDate(date);
  };

  if (loading) return <Loading />;

  return (
    <div className="mt-4 flex w-full flex-col">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-subtitle font-semibold text-neutral-150">
            API Credentials
          </h2>
          <p className="mt-1 text-caption text-neutral-110">
            Create and manage API credentials for programmatic access to your
            account.
          </p>
        </div>
        <Button
          title="Create API Credential"
          IconComponent={PlusSmIcon}
          size="small"
          iconPosition="right"
          theme="primary"
          onClick={handleCreateApiCredential}
        />
      </div>

      <Transition show={!loading && !error} {...defaultTransitionProps}>
        {apiCredentials.length === 0 ? (
          <div className="flex h-64 items-center justify-center rounded-lg border border-neutral-50 bg-neutral-25">
            <div className="text-center">
              <p className="text-body text-neutral-125">
                No API credentials yet
              </p>
              <p className="mt-1 text-caption text-neutral-110">
                Create your first API credential to get started.
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-neutral-50">
            <table className="min-w-full table-fixed divide-y divide-neutral-50">
              <colgroup>
                <col className="w-[30%]" />
                <col className="w-[20%]" />
                <col className="w-[15%]" />
                <col className="w-[15%]" />
                <col className="w-[15%]" />
                <col className="w-[5%]" />
              </colgroup>
              <thead className="bg-neutral-25">
                <tr>
                  <th className="px-6 py-3 text-left text-small-caption font-medium text-neutral-125">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-small-caption font-medium text-neutral-125">
                    Client ID
                  </th>
                  <th className="px-6 py-3 text-left text-small-caption font-medium text-neutral-125">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-small-caption font-medium text-neutral-125">
                    Last Used
                  </th>
                  <th className="px-6 py-3 text-left text-small-caption font-medium text-neutral-125">
                    Status
                  </th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-50 bg-white">
                {apiCredentials.map((credential, index) => (
                  <tr
                    key={credential.id}
                    className="whitespace-nowrap hover:bg-neutral-25"
                  >
                    <td className="overflow-hidden px-6 py-4 text-caption text-neutral-125">
                      <div className="flex max-w-[300px] flex-col truncate">
                        <span className="truncate">{credential.name}</span>
                        {credential.description && (
                          <span
                            className="truncate text-small-caption text-neutral-110"
                            title={credential.description}
                          >
                            {credential.description}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="overflow-hidden px-6 py-4">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="font-mono min-w-0 flex-1 truncate text-small-caption text-neutral-125">
                          {credential.apiClientId}
                        </span>
                        <IconButton
                          IconComponent={
                            copiedIndex === index ? CheckIcon : ClipboardIcon
                          }
                          iconClassName={classNames(
                            'h-4 w-4',
                            copiedIndex === index && 'text-green-150',
                          )}
                          aria-label="Copy Client ID"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(credential.apiClientId).then(
                              (success) => {
                                if (success) {
                                  setCopiedIndex(index);
                                  setTimeout(() => setCopiedIndex(null), 2000);
                                } else {
                                  toast.custom(({ visible }) => (
                                    <ToastAlert
                                      isVisible={visible}
                                      message="Failed to copy to clipboard. Your browser may require HTTPS or clipboard permissions."
                                      level="error"
                                    />
                                  ));
                                }
                              },
                            );
                          }}
                        />
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-caption text-neutral-125">
                      {formatDate(credential.createdAt)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-caption text-neutral-110">
                      {formatDate(credential.lastUsedAt)}
                    </td>
                    <td className="px-6 py-4">
                      {credential.status ===
                      ProvidersApiCredentialStatus.Active ? (
                        <span className="inline-flex rounded-full bg-green-50 px-2.5 py-0.5 text-small-caption font-medium text-green-125">
                          Active
                        </span>
                      ) : (
                        <span className="text-small-caption text-neutral-110">
                          {credential.deactivatedAt
                            ? `Deactivated ${getTimeStamp(
                                credential.deactivatedAt,
                              )}`
                            : 'Deactivated'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {credential.status ===
                        ProvidersApiCredentialStatus.Active && (
                        <IconButton
                          IconComponent={TrashIcon}
                          iconClassName="h-5 w-5 text-red-125"
                          aria-label="Deactivate API credential"
                          onClick={() => handleDeactivateClick(credential)}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Transition>

      {/* Create Credential Modal - Shows form or new credential */}
      <Modal
        isOpen={isCreateModalOpen}
        setClosed={handleCloseCreateModal}
        width="medium"
      >
        <div className="px-10 py-8">
          {newCredential ? (
            // Show new credential content
            <div className="space-y-6">
              <div className="flex flex-col items-center">
                <div className="mb-1 flex text-center font-serif text-subtitle-small text-green-150">
                  API Credential Created
                </div>
                <p className="text-center text-caption text-neutral-110">
                  Copy and save these credentials securely
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-small-caption font-medium text-neutral-125">
                    Client ID
                  </label>
                  <div className="flex items-center gap-2">
                    <code className="font-mono flex-1 rounded bg-neutral-25 px-3 py-2 text-body text-neutral-150">
                      {newCredential.apiClientId}
                    </code>
                    <button
                      onClick={() => {
                        copyToClipboard(newCredential.apiClientId).then(
                          (success) => {
                            if (success) {
                              setCopiedField('clientId');
                              setTimeout(() => setCopiedField(null), 2000);
                            } else {
                              toast.custom(({ visible }) => (
                                <ToastAlert
                                  isVisible={visible}
                                  message="Failed to copy to clipboard. Your browser may require HTTPS or clipboard permissions."
                                  level="error"
                                />
                              ));
                            }
                          },
                        );
                      }}
                      className={classNames(
                        'transition-colors',
                        copiedField === 'clientId'
                          ? 'text-green-150'
                          : 'text-neutral-110 hover:text-neutral-125',
                      )}
                      aria-label="Copy Client ID"
                    >
                      {copiedField === 'clientId' ? (
                        <CheckIcon className="h-5 w-5" />
                      ) : (
                        <ClipboardIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-small-caption font-medium text-neutral-125">
                    Secret
                  </label>
                  <div className="flex items-center gap-2">
                    <code className="font-mono flex-1 break-all rounded bg-neutral-25 px-3 py-2 text-body text-neutral-150">
                      {newCredential.secret}
                    </code>
                    <button
                      onClick={() => {
                        copyToClipboard(newCredential.secret).then(
                          (success) => {
                            if (success) {
                              setCopiedField('secret');
                              setTimeout(() => setCopiedField(null), 2000);
                            } else {
                              toast.custom(({ visible }) => (
                                <ToastAlert
                                  isVisible={visible}
                                  message="Failed to copy to clipboard. Your browser may require HTTPS or clipboard permissions."
                                  level="error"
                                />
                              ));
                            }
                          },
                        );
                      }}
                      className={classNames(
                        'flex-shrink-0 transition-colors',
                        copiedField === 'secret'
                          ? 'text-green-150'
                          : 'text-neutral-110 hover:text-neutral-125',
                      )}
                      aria-label="Copy Secret"
                    >
                      {copiedField === 'secret' ? (
                        <CheckIcon className="h-5 w-5" />
                      ) : (
                        <ClipboardIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  <p className="mt-1.5 text-small-caption italic text-neutral-110">
                    This secret is shown once and won't be available again
                  </p>
                </div>
              </div>

              <div className="border-t border-neutral-50 pt-4">
                <p className="text-center text-caption text-neutral-110">
                  Store these credentials securely and never share them
                  publicly.
                </p>
              </div>

              <div className="flex w-full flex-row items-center justify-center pt-2">
                <Button
                  title="Done"
                  theme="primary"
                  onClick={handleCloseCreateModal}
                />
              </div>
            </div>
          ) : (
            // Show create form
            <div>
              <div className="flex flex-col items-center">
                <div className="mb-4 flex text-center font-serif text-subtitle-small text-green-150">
                  Create New API Credential
                </div>
                <div className="mb-6 text-center text-body text-green-125">
                  Enter a name for this API credential. You can use this to
                  identify it later.
                </div>
              </div>
              <div className="mb-6 space-y-4">
                <InputGroup
                  label="Name"
                  placeholder="Enter a name for this API credential"
                  errorMessage={errors.name?.message}
                  useNaturalLettering
                  {...register('name', {
                    required: 'Please enter a name for the API credential',
                  })}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCreateApiCredentialSubmit();
                    }
                  }}
                />
                <InputGroup
                  label="Description (optional)"
                  placeholder="Optional description for this credential"
                  errorMessage={errors.description?.message}
                  useNaturalLettering
                  {...register('description')}
                />
              </div>
              <div className="flex w-full flex-row items-center justify-center">
                <Button
                  title="Cancel"
                  theme="secondary"
                  className="mr-5"
                  onClick={handleCloseCreateModal}
                  disabled={isCreating}
                />
                <Button
                  title={isCreating ? 'Creating...' : 'Create'}
                  theme="primary"
                  onClick={handleCreateApiCredentialSubmit}
                  disabled={isCreating}
                  {...(isCreating && {
                    IconComponent: Spinner,
                    iconClassName: 'h-[16px] w-[16px]',
                  })}
                />
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Confirm Deactivate Modal */}
      <Modal
        isOpen={isConfirmDeactivateModalOpen}
        setClosed={() => {
          setIsConfirmDeactivateModalOpen(false);
          setCredentialToDeactivate(null);
        }}
        width="small"
      >
        <div className="px-10 py-8">
          <div className="flex flex-col items-center">
            <div className="mb-2 flex text-center font-serif text-subtitle-small text-green-150">
              Deactivate API Credential?
            </div>
            <div className="text-center text-body text-green-125">
              Deactivating "{credentialToDeactivate?.name}" will prevent it from
              being used. This action cannot be undone.
            </div>
          </div>
          <div className="mt-11 flex w-full flex-row items-center justify-center">
            <Button
              title="Cancel"
              theme="secondary"
              className="mr-5"
              onClick={() => {
                setIsConfirmDeactivateModalOpen(false);
                setCredentialToDeactivate(null);
              }}
            />
            <Button
              title="Deactivate"
              theme="primary"
              onClick={handleConfirmDeactivate}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DevelopersSection;
